import { useT } from "@/lib/i18n";
import teamJesus from "@/assets/team-jesus.jpg";
import teamLucia from "@/assets/team-lucia.jpg";
import teamAlvaro from "@/assets/team-alvaro.jpg";
import teamMarta from "@/assets/team-marta.jpg";
import teamSergio from "@/assets/team-sergio.jpg";
import teamCarmen from "@/assets/team-carmen.jpg";

export type TeamMember = {
  slug: string;
  name: string;
  role: string;
  shortRole: string;
  bio: string;
  specialties: string[];
  portrait: { url: string; alt: string };
  /**
   * Generic per-slot media key (team.1.portrait … team.6.portrait). Slots are
   * stable — people change, but slot N keeps its key, so the backoffice never
   * has to migrate when someone joins or leaves.
   */
  portraitKey: string;
  featured: boolean;
};

export function useTeam(): TeamMember[] {
  const { tk } = useT();

  return [
    {
      slug: "jesus-fundador",
      name: tk("team.jesus.name", "Pablo García"),
      role: tk("team.jesus.role", "Fundador y Director del parque"),
      shortRole: tk("team.jesus.shortRole", "Fundador"),
      bio: tk(
        "team.jesus.bio",
        "El primero que se subió a un árbol de La Juliana con un arnés y una idea: traer a Andalucía el primer parque de aventura entre los árboles. Hoy sigue recorriendo los circuitos cada semana para que todo esté como el primer día."
      ),
      specialties: [
        tk("team.jesus.s1", "Visión y diseño del parque"),
        tk("team.jesus.s2", "Relación con escuelas y empresas"),
        tk("team.jesus.s3", "Cultura de seguridad"),
      ],
      portrait: { url: teamJesus, alt: "Pablo García, fundador de Bosque Suspendido" },
      portraitKey: "team.1.portrait",
      featured: true,
    },
    {
      slug: "lucia-seguridad",
      name: tk("team.lucia.name", "Lucía Romero"),
      role: tk("team.lucia.role", "Responsable de Seguridad y Jefa de Monitores"),
      shortRole: tk("team.lucia.shortRole", "Seguridad"),
      bio: tk(
        "team.lucia.bio",
        "Técnica en trabajos verticales y formadora de todo el equipo de monitores. Revisa líneas de vida, arneses y protocolos a diario: si Lucía da el visto bueno, puedes lanzarte tranquilo."
      ),
      specialties: [
        tk("team.lucia.s1", "Protocolos y auditorías de seguridad"),
        tk("team.lucia.s2", "Formación de monitores"),
        tk("team.lucia.s3", "Sistemas de línea de vida continua"),
      ],
      portrait: { url: teamLucia, alt: "Lucía Romero, responsable de seguridad" },
      portraitKey: "team.2.portrait",
      featured: false,
    },
    {
      slug: "alvaro-monitor",
      name: tk("team.alvaro.name", "Álvaro Castro"),
      role: tk("team.alvaro.role", "Instructor de Tirolinas y Circuitos"),
      shortRole: tk("team.alvaro.shortRole", "Monitor"),
      bio: tk(
        "team.alvaro.bio",
        "Si escuchas un '¡vamos, que tú puedes!' desde una plataforma a 12 metros, es Álvaro. Especialista en convertir el miedo a las alturas en la mejor anécdota del día, sobre todo con los más peques."
      ),
      specialties: [
        tk("team.alvaro.s1", "Tirolinas y salto de Tarzán"),
        tk("team.alvaro.s2", "Circuitos infantiles y junior"),
        tk("team.alvaro.s3", "Primeros auxilios en altura"),
      ],
      portrait: { url: teamAlvaro, alt: "Álvaro Castro, instructor de tirolinas" },
      portraitKey: "team.3.portrait",
      featured: false,
    },
    {
      slug: "marta-eventos",
      name: tk("team.marta.name", "Marta Delgado"),
      role: tk("team.marta.role", "Coordinadora de Grupos y Eventos"),
      shortRole: tk("team.marta.shortRole", "Grupos y eventos"),
      bio: tk(
        "team.marta.bio",
        "Organiza cumpleaños, despedidas, excursiones escolares y jornadas de empresa sin que se le escape un detalle. Llega con tu grupo y Marta ya tendrá los arneses, la merienda y el plan B por si llueve."
      ),
      specialties: [
        tk("team.marta.s1", "Excursiones escolares y campamentos"),
        tk("team.marta.s2", "Cumpleaños y celebraciones"),
        tk("team.marta.s3", "Team building para empresas"),
      ],
      portrait: { url: teamMarta, alt: "Marta Delgado, coordinadora de grupos y eventos" },
      portraitKey: "team.4.portrait",
      featured: false,
    },
    {
      slug: "sergio-mantenimiento",
      name: tk("team.sergio.name", "Sergio Vidal"),
      role: tk("team.sergio.role", "Técnico de Mantenimiento de Circuitos"),
      shortRole: tk("team.sergio.shortRole", "Mantenimiento"),
      bio: tk(
        "team.sergio.bio",
        "Cada mañana, antes de abrir, Sergio recorre los 9 circuitos comprobando cables, plataformas y poleas. Conoce cada pino del parque por su nombre (o casi) y cuida de que el bosque y los juegos convivan en paz."
      ),
      specialties: [
        tk("team.sergio.s1", "Inspección diaria de circuitos"),
        tk("team.sergio.s2", "Cuidado del arbolado"),
        tk("team.sergio.s3", "Material de escalada y poleas"),
      ],
      portrait: { url: teamSergio, alt: "Sergio Vidal, técnico de mantenimiento" },
      portraitKey: "team.5.portrait",
      featured: false,
    },
    {
      slug: "carmen-recepcion",
      name: tk("team.carmen.name", "Carmen Ortiz"),
      role: tk("team.carmen.role", "Recepción, Reservas y Atención al Visitante"),
      shortRole: tk("team.carmen.shortRole", "Recepción"),
      bio: tk(
        "team.carmen.bio",
        "La primera sonrisa que te recibe en la cabaña de entrada. Gestiona reservas, resuelve dudas por WhatsApp y se asegura de que cada familia salga del bosque con ganas de volver."
      ),
      specialties: [
        tk("team.carmen.s1", "Reservas y bonos regalo"),
        tk("team.carmen.s2", "Atención a familias y grupos"),
        tk("team.carmen.s3", "WhatsApp y consultas"),
      ],
      portrait: { url: teamCarmen, alt: "Carmen Ortiz, recepción y reservas" },
      portraitKey: "team.6.portrait",
      featured: false,
    },
  ];
}
